'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { studentApi, type StudentContext as Ctx } from '@/lib/api/student';

const DEFAULT: Ctx = {
  partner: null,
  capabilities: {
    tests: false,
    courses: false,
    certificates: false,
    platformCatalog: false,
  },
};

type ContextValue = Ctx & {
  loading: boolean;
  error: boolean;
  /** Re-fetches /student/context without flipping `loading` (no flash of the B2C state). */
  refresh: () => Promise<void>;
};

const StudentPartnerContext = createContext<ContextValue>({
  ...DEFAULT,
  loading: true,
  error: false,
  refresh: async () => {},
});

export function StudentPartnerProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [ctx, setCtx] = useState<Ctx>(DEFAULT);
  const [loading, setLoading] = useState(true);
  // Distinguishes "context fetch errored" from "successfully resolved to no
  // partner". Consumers that gate on capabilities (e.g. useCoursesComingSoon)
  // can then fail OPEN on error instead of gating a partner out of paid content.
  const [error, setError] = useState(false);

  // Only the latest fetch may write state, so a slow initial load cannot overwrite a refresh.
  const fetchSeq = useRef(0);

  const invalidateInFlight = useCallback(() => {
    fetchSeq.current++;
  }, []);

  const fetchContext = useCallback(async (silent: boolean) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const c = await studentApi.getContext();
      if (seq !== fetchSeq.current) return;
      setCtx(c);
      setError(false);
    } catch {
      if (seq !== fetchSeq.current) return;
      // A failed silent refresh keeps what we had; only the initial load falls back.
      if (silent) return;
      setCtx(DEFAULT);
      setError(true);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      invalidateInFlight();
      setCtx(DEFAULT);
      setError(false);
      setLoading(false);
      return;
    }

    void fetchContext(false);
    return invalidateInFlight;
  }, [isAuthenticated, fetchContext, invalidateInFlight]);

  const refresh = useCallback(() => fetchContext(true), [fetchContext]);

  return (
    <StudentPartnerContext.Provider value={{ ...ctx, loading, error, refresh }}>
      {children}
    </StudentPartnerContext.Provider>
  );
}

export function useStudentPartner() {
  return useContext(StudentPartnerContext);
}
